import { AppState } from '../reducers';

// services
import * as Services from '../../services/ProviderConsumptions.Services';
import { GetProviderConsumption } from '../../services/ProviderConsumptionImport.Services';

// functions
import { chunkIds, EXPORT_MAX_PAGES, EXPORT_PAGE_SIZE, mergeToSettleResults } from '../../functions/ProviderConsumptions.Functions';
import { toErrorType } from './ProviderConsumptionImport.Middleware';

// models
import {
  ProviderConsumption,
  ProviderConsumptionsFilter,
  ProviderConsumptionsPage,
  ProviderConsumptionTotals,
  ProviderSyncAlarmsFilter,
  ProviderSyncAlarmsPage,
  ProviderSyncRequestResult,
  ProviderSyncRun,
  ProviderToSettleRequest,
  ProviderToSettleResult,
  RevalueImportedRequest,
  RevalueImportedResult,
} from '../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { ErrorType } from '../../models/HttpError';

/**
 * Grilla unificada de consumos del proveedor (spec 006). El token se lee sólo acá; los servicios lo
 * reciben por parámetro. Los thunks devuelven promesas (sin estado en el store) y rechazan con ErrorType.
 */

export type ProviderConsumptionsThunk<TResult> = (dispatch: (action: unknown) => void, getState: () => AppState) => Promise<TResult>;

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

const run = async <T>(getState: () => AppState, call: (token: string) => Promise<T>): Promise<T> => {
  const token = getState().oidc?.user?.access_token;
  if (!token) throw NotAuthorized;
  try {
    return await call(token);
  } catch (error: unknown) {
    throw toErrorType(error);
  }
};

export const actionCreators = {
  /** Página de la grilla (Provider.id int). */
  LoadProviderConsumptions:
    (providerId: number, filter: ProviderConsumptionsFilter): ProviderConsumptionsThunk<ProviderConsumptionsPage> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetProviderConsumptions(token, providerId, filter)),

  /** Totales por moneda y alarmas con los mismos filtros. */
  LoadProviderConsumptionTotals:
    (providerId: number, filter: ProviderConsumptionsFilter): ProviderConsumptionsThunk<ProviderConsumptionTotals> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetProviderConsumptionTotals(token, providerId, filter)),

  /**
   * Todos los consumos del filtro y orden actuales, para exportar: pide páginas de 500 hasta la última
   * (o hasta EXPORT_MAX_PAGES).
   */
  LoadAllProviderConsumptions:
    (providerId: number, filter: ProviderConsumptionsFilter): ProviderConsumptionsThunk<ProviderConsumption[]> =>
    async (_dispatch, getState) =>
      run(getState, async (token) => {
        const all: ProviderConsumption[] = [];
        for (let pageNumber = 1; pageNumber <= EXPORT_MAX_PAGES; pageNumber += 1) {
          const page = await Services.GetProviderConsumptions(token, providerId, { ...filter, pageNumber, pageSize: EXPORT_PAGE_SIZE });
          all.push(...page.items);
          if (page.items.length === 0 || pageNumber >= page.totalPages || all.length >= page.totalRecords) break;
        }
        return all;
      }),

  /** Última corrida (null si nunca se sincronizó). */
  LoadLastProviderSync:
    (providerId: number): ProviderConsumptionsThunk<ProviderSyncRun | null> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetLastProviderSync(token, providerId)),

  /** Historial de corridas (más nueva primero). */
  LoadProviderSyncRuns:
    (providerId: number, take?: number): ProviderConsumptionsThunk<ProviderSyncRun[]> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetProviderSyncRuns(token, providerId, take)),

  /** Alarmas paginadas de una corrida. */
  LoadProviderSyncAlarms:
    (runId: string, filter: ProviderSyncAlarmsFilter): ProviderConsumptionsThunk<ProviderSyncAlarmsPage> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.GetProviderSyncAlarms(token, runId, filter)),

  /** Pide la sincronización (general o de un protocolo); 409 → `{ conflict: true }`. */
  RequestProviderSync:
    (providerId: number, protocolId?: number): ProviderConsumptionsThunk<ProviderSyncRequestResult> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.RequestProviderSync(token, providerId, protocolId)),

  /** Revaloriza los consumos importados (origen 5) del proveedor o de un archivo (spec 008). */
  RevalueImportedConsumptions:
    (providerId: number, body: RevalueImportedRequest = {}): ProviderConsumptionsThunk<RevalueImportedResult> =>
    async (_dispatch, getState) =>
      run(getState, (token) => Services.RevalueImportedConsumptions(token, providerId, body)),

  /**
   * Marca / quita "Por liquidar" (MP 032, spec 010). Por ids: lotes de 1000 y resultado sumado (sin ids no llama);
   * por filtro: una sola llamada. Rechaza con ErrorType (los lotes anteriores al error ya quedaron aplicados).
   */
  MarkProviderConsumptionsToSettle:
    (request: ProviderToSettleRequest): ProviderConsumptionsThunk<ProviderToSettleResult> =>
    async (_dispatch, getState) =>
      run(getState, async (token) => {
        if (!('ids' in request)) return Services.MarkProviderConsumptionsToSettle(token, request);
        const results: ProviderToSettleResult[] = [];
        for (const ids of chunkIds(request.ids)) {
          results.push(await Services.MarkProviderConsumptionsToSettle(token, { ids, toSettle: request.toSettle }));
        }
        return mergeToSettleResults(results);
      }),

  /** Un consumo de proveedor por id para el drawer de detalle (spec 010, US8): GET protocols/providers/consumptions/{id}. */
  LoadProviderConsumption:
    (id: string): ProviderConsumptionsThunk<ProviderConsumption | undefined> =>
    async (_dispatch, getState) =>
      run(getState, async (token) => (await GetProviderConsumption(token, id)) as ProviderConsumption | undefined),
};
